import React, { useEffect, useMemo, useState } from "react";
import { Accordion, Button, Dropdown } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useGetMetrosQuery } from "../../../api/metro";
import { useGetStoresQuery } from "../../../api/stores";
import { useGetShiftTemplatesQuery } from "../../../api/shiftTemplate";
import {
  useGetSchedulesByMetroQuery,
  useUpsertScheduleMutation,
} from "../../../api/shiftDailySchedule";
import CardLayout from "../../../components/layouts/CardLayout";
import "./style.scss";

const getWeekMonday = (offset = 0) => {
  const today = new Date();
  const todayDay = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((todayDay + 6) % 7) + offset * 7);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const toDateString = (date) => date.toISOString().split("T")[0];

const DEFAULT_ROW = {
  rowId: "",
  zoneId: "",
  zoneName: "",
  startTime: "07:00 AM",
  noOfShifts: 1,
  shiftTemplateId: "",
};

const getScheduleForDay = (hubSchedules, dayDate, toDateStr) => {
  const dateStr = toDateStr(dayDate);
  return hubSchedules.find((s) => {
    const sDate = s.scheduleDate ? new Date(s.scheduleDate) : null;
    return sDate && toDateStr(sDate) === dateStr;
  });
};

const HubCalendarView = ({ dayTabs, selectedDay, onDaySelect, hubSchedules, weekOffset, onWeekChange, isReadOnly }) => {
  const toDateStr = (d) => d.toISOString().split("T")[0];
  const monday = getWeekMonday(weekOffset);
  const weekLabel = `${monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  return (
    <div className="scheduling-assistant-page__calendar">
      <div className="scheduling-assistant-page__calendar-week-nav">
        <button
          type="button"
          aria-label="Previous week"
          onClick={() => onWeekChange(-1)}
          disabled={isReadOnly}
        >
          <FaChevronLeft />
        </button>
        <span className="scheduling-assistant-page__calendar-week-label">{weekLabel}</span>
        <button
          type="button"
          aria-label="Next week"
          onClick={() => onWeekChange(1)}
        >
          <FaChevronRight />
        </button>
      </div>
      <div className="scheduling-assistant-page__calendar-grid">
        {dayTabs.map(({ name, label, date, isToday }) => {
          const schedule = getScheduleForDay(hubSchedules, date, toDateStr);
          const shiftCount = schedule?.rows?.reduce((sum, r) => sum + (Number(r.noOfShifts) || 0), 0) ?? 0;
          const isSelected = selectedDay === name;
          return (
            <button
              type="button"
              key={name}
              className={[
                "scheduling-assistant-page__calendar-day",
                isSelected ? "active" : "",
                isToday ? "today" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onDaySelect(name)}
            >
              <span className="scheduling-assistant-page__calendar-day-name">{name.slice(0, 3)}</span>
              <span className="scheduling-assistant-page__calendar-day-date">{label}</span>
              {shiftCount > 0 && (
                <span className="scheduling-assistant-page__calendar-day-badge">{shiftCount} shifts</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const HubScheduleTable = ({
  hub,
  hubSchedules,
  dayInfo,
  dayTabs,
  selectedDay,
  onDaySelect,
  weekOffset,
  onWeekChange,
  selectedMetro,
  selectedMetroObj,
  onSaveSuccess,
  templateOptions,
  isReadOnly,
  isFetchingTemplates,
  upsertSchedule,
  isSaving,
}) => {
  const scheduleForDay = dayInfo ? getScheduleForDay(hubSchedules, dayInfo.date, (d) => d.toISOString().split("T")[0]) : null;
  const dateStr = dayInfo?.date?.toISOString().split("T")[0] ?? "";
  const makeRowId = (i) => (dateStr ? `row_${dateStr}_${i + 1}` : `row_${i + 1}`);

  const [validationErrors, setValidationErrors] = useState(null);

  const [rows, setRows] = useState(() =>
    scheduleForDay?.rows?.length
      ? scheduleForDay.rows.map((r, i) => ({
          rowId: makeRowId(i),
          zoneId: r.zoneId || "",
          zoneName: r.zoneName || r.zone || "",
          startTime: r.startTime || "07:00 AM",
          noOfShifts: r.noOfShifts ?? 1,
          shiftTemplateId: r.shiftTemplateId?._id || r.shiftTemplateId || "",
        }))
      : [{ ...DEFAULT_ROW, rowId: makeRowId(0) }]
  );

  useEffect(() => {
    if (scheduleForDay?.rows?.length) {
      setRows(
        scheduleForDay.rows.map((r, i) => ({
          rowId: makeRowId(i),
          zoneId: r.zoneId || "",
          zoneName: r.zoneName || r.zone || "",
          startTime: r.startTime || "07:00 AM",
          noOfShifts: r.noOfShifts ?? 1,
          shiftTemplateId: r.shiftTemplateId?._id || r.shiftTemplateId || "",
        }))
      );
    } else {
      setRows([{ ...DEFAULT_ROW, rowId: makeRowId(0) }]);
    }
  }, [scheduleForDay, dayInfo?.name, dateStr]);

  useEffect(() => {
    setValidationErrors(null);
  }, [dayInfo?.name]);

  const updateRow = (index, field, value) => {
    setValidationErrors(null);
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addRow = () => {
    setRows((prev) => [...prev, { ...DEFAULT_ROW, rowId: makeRowId(prev.length) }]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      return next.map((r, i) => ({ ...r, rowId: makeRowId(i) }));
    });
  };

  const getRowValidationErrors = (row, idx) => {
    const errs = [];
    if (!String(row.startTime || "").trim()) errs.push("time");
    if (!String(row.zoneName || row.zone || "").trim()) errs.push("zone");
    if (!row.noOfShifts || Number(row.noOfShifts) < 1) errs.push("noOfShifts");
    if (!String(row.shiftTemplateId || "").trim()) errs.push("shiftTemplate");
    return errs;
  };

  const handleSave = async () => {
    if (!dayInfo || !selectedMetroObj || !hub) return;
    setValidationErrors(null);
    const rowErrors = rows.map((r, i) => ({ idx: i, fields: getRowValidationErrors(r, i) }));
    const invalidRows = rowErrors.filter((e) => e.fields.length > 0);
    if (invalidRows.length > 0) {
      setValidationErrors(
        invalidRows.reduce((acc, { idx, fields }) => ({ ...acc, [idx]: fields }), {})
      );
      return;
    }
    const d = new Date(dayInfo.date);
    d.setHours(0, 0, 0, 0);
    const scheduleDate = d.toISOString();
    const totalCapacity = rows.reduce((sum, r) => sum + (Number(r.noOfShifts) || 0), 0);
    const payload = {
      metroId: selectedMetroObj._id,
      hub: hub.id,
      scheduleDate,
      totalCapacity,
      rows: rows.map((r, i) => {
        const zoneName = r.zoneName || r.zone || "";
        const zoneId = zoneName ? `z_${selectedMetro}_${hub.name}_${zoneName}` : "";
        return {
          rowId: r.rowId || makeRowId(i),
          zoneId,
          zoneName,
          startTime: r.startTime || "07:00 AM",
          noOfShifts: Number(r.noOfShifts) || 0,
          shiftTemplateId: r.shiftTemplateId || undefined,
        };
      }),
    };
    try {
      await upsertSchedule(payload).unwrap();
      onSaveSuccess?.();
    } catch (err) {
      console.error("Save schedule error:", err);
    }
  };

  return (
    <div className="scheduling-assistant-page__hub-content">
      <HubCalendarView
        dayTabs={dayTabs}
        selectedDay={selectedDay}
        onDaySelect={onDaySelect}
        hubSchedules={hubSchedules}
        weekOffset={weekOffset}
        onWeekChange={onWeekChange}
        isReadOnly={isReadOnly}
      />
      <div className={`scheduling-assistant-page__table-wrap${isReadOnly ? " is-readonly" : ""}`}>
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Zone</th>
              <th># of Shifts</th>
              <th>Shift Template</th>
              {!isReadOnly && <th style={{ width: 48 }} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const errs = validationErrors?.[idx] || [];
              return (
              <tr key={row.rowId || idx}>
                <td>
                  <input
                    type="text"
                    value={row.startTime}
                    onChange={(e) => updateRow(idx, "startTime", e.target.value)}
                    disabled={isReadOnly}
                    className={`form-control${errs.includes("time") ? " is-invalid" : ""}`}
                    required
                    aria-invalid={errs.includes("time")}
                    placeholder="e.g. 07:00 AM"
                  />
                </td>
                <td>
                  <select
                    value={row.zoneName || row.zone || ""}
                    onChange={(e) => updateRow(idx, "zoneName", e.target.value)}
                    disabled={isReadOnly}
                    className={errs.includes("zone") ? "is-invalid" : ""}
                    required
                    aria-invalid={errs.includes("zone")}
                  >
                    <option value="">Select zone</option>
                    {["A", "B", "C", "D", "E", "F", "G"].map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={row.noOfShifts}
                    onChange={(e) => updateRow(idx, "noOfShifts", Number(e.target.value))}
                    disabled={isReadOnly}
                    className={errs.includes("noOfShifts") ? "is-invalid" : ""}
                    required
                    aria-invalid={errs.includes("noOfShifts")}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                    <select
                      value={row.shiftTemplateId}
                      onChange={(e) => updateRow(idx, "shiftTemplateId", e.target.value)}
                      disabled={isFetchingTemplates || isReadOnly}
                      className={errs.includes("shiftTemplate") ? "is-invalid" : ""}
                      required
                      aria-invalid={errs.includes("shiftTemplate")}
                    >
                      <option value="">
                        {isFetchingTemplates ? "Loading..." : "Select template"}
                      </option>
                      {templateOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}
                        </option>
                      ))}
                    </select>
                  </td>
                  {!isReadOnly && (
                    <td className="scheduling-assistant-page__row-actions">
                      <button
                        type="button"
                        className="scheduling-assistant-page__delete-row"
                        onClick={() => removeRow(idx)}
                        disabled={rows.length <= 1}
                        aria-label="Delete row"
                        title="Delete row"
                      >
                        ×
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {validationErrors && Object.keys(validationErrors).length > 0 && (
        <div className="scheduling-assistant-page__validation-msg text-danger small mt-2">
          Please fill in all required fields: Time, Zone, # of Shifts, and Shift Template for each row.
        </div>
      )}
      {!isReadOnly && (
        <div className="scheduling-assistant-page__footer">
          <Button variant="outline-secondary" onClick={addRow}>
            + Add Row
          </Button>
          <div className="scheduling-assistant-page__footer-actions">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Scheduling = () => {
  const [selectedMetro, setSelectedMetro] = useState("");
  const [visibleHubIds, setVisibleHubIds] = useState([]);
  const [expandedHubs, setExpandedHubs] = useState([]);

  const [weekOffset, setWeekOffset] = useState(1);
  const isReadOnly = getWeekMonday(weekOffset) < getWeekMonday(1);

  const todayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][new Date().getDay()];
  const [selectedDay, setSelectedDay] = useState(todayName);

  const currentUser = useSelector((state) => state.auth.currentUser);
  const { data: metros, isFetching: isFetchingMetros } = useGetMetrosQuery(null, {
    refetchOnMountOrArgChange: true,
  });
  const { data: stores, isFetching: isFetchingStores } = useGetStoresQuery(null, {
    refetchOnMountOrArgChange: true,
  });
  const { data: shiftTemplates, isFetching: isFetchingTemplates } = useGetShiftTemplatesQuery(null, {
    refetchOnMountOrArgChange: true,
  });

  const selectedMetroObj = useMemo(() => {
    if (!Array.isArray(metros) || !selectedMetro) return null;
    return metros.find((m) => m?.name === selectedMetro);
  }, [metros, selectedMetro]);

  const metroTabs = useMemo(() => {
    if (currentUser?.role === "metro_manager") {
      const metroManagerMetros = JSON.parse(localStorage.getItem("user_metro") || "[]");
      return Array.isArray(metroManagerMetros) ? metroManagerMetros.filter(Boolean) : [];
    }
    if (!Array.isArray(metros)) return [];
    return metros.map((item) => item?.name).filter(Boolean);
  }, [metros, currentUser]);

  useEffect(() => {
    if (!metroTabs.length) return;
    if (!selectedMetro || !metroTabs.includes(selectedMetro)) {
      setSelectedMetro(metroTabs[0]);
    }
  }, [metroTabs, selectedMetro]);

  const monday = getWeekMonday(weekOffset);
  const dayTabs = useMemo(() => {
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return days.map((name, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return {
        name,
        label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        date,
        isToday: date.toDateString() === now.toDateString(),
      };
    });
  }, [weekOffset, monday]);

  const hubList = useMemo(() => {
    if (!Array.isArray(stores) || !selectedMetro) return [];
    return stores
      .filter((store) => store?.metro === selectedMetro)
      .map((store) => ({ id: store._id, name: store.name }))
      .filter((h) => h.name);
  }, [stores, selectedMetro]);

  const fromDate = useMemo(() => toDateString(monday), [monday]);
  const toDate = useMemo(() => {
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return toDateString(sunday);
  }, [monday]);

  const { data: schedules = [], isFetching: isFetchingSchedules, refetch: refetchSchedules } = useGetSchedulesByMetroQuery(
    {
      metro: selectedMetroObj?._id,
      fromDate,
      toDate,
    },
    { skip: !selectedMetroObj?._id }
  );

  const [upsertSchedule, { isLoading: isSaving }] = useUpsertScheduleMutation();

  const schedulesByHub = useMemo(() => {
    const map = {};
    hubList.forEach((hub) => {
      map[hub.id] = { hub, schedules: [] };
    });
    (Array.isArray(schedules) ? schedules : []).forEach((s) => {
      const hubId = s.hub?._id || s.hub;
      if (hubId && map[hubId]) {
        map[hubId].schedules.push(s);
      }
    });
    return map;
  }, [schedules, hubList]);

  const templateOptions = useMemo(() => {
    if (!Array.isArray(shiftTemplates)) return [];
    return shiftTemplates
      .filter((t) => !selectedMetro || t?.metro === selectedMetro)
      .map((t) => ({ id: t._id, title: t.title }))
      .filter((t) => t.title);
  }, [shiftTemplates, selectedMetro]);

  const dayInfo = dayTabs.find((d) => d.name === selectedDay);

  useEffect(() => {
    if (hubList.length > 0 && visibleHubIds.length === 0) {
      setVisibleHubIds([hubList[0].id]);
      setExpandedHubs([hubList[0].id]);
    }
  }, [hubList, selectedMetro]);

  const visibleHubs = useMemo(
    () => hubList.filter((h) => visibleHubIds.includes(h.id)),
    [hubList, visibleHubIds]
  );
  const availableHubsToAdd = useMemo(
    () => hubList.filter((h) => !visibleHubIds.includes(h.id)),
    [hubList, visibleHubIds]
  );
  const addHub = (hubId) => {
    setVisibleHubIds((prev) => [...prev, hubId]);
    setExpandedHubs((prev) => [...prev, hubId]);
  };
  const removeHub = (hubId) => {
    setVisibleHubIds((prev) => prev.filter((id) => id !== hubId));
    setExpandedHubs((prev) => prev.filter((id) => id !== hubId));
  };

  return (
    <CardLayout>
      <div className="scheduling-assistant-page">
        <div className="scheduling-assistant-page__disclaimer">
          <span>⏰</span>
          <p>
            Shifts will be automatically created at{" "}
            <strong>12:00 AM CST every Wednesday</strong> based on the configuration below.
          </p>
        </div>

        <div className="scheduling-assistant-page__topbar">
          <div className="scheduling-assistant-page__title-wrap">
            <span className="scheduling-assistant-page__title-dot" />
            <h5>Shift Planning</h5>
          </div>
          <div className="scheduling-assistant-page__metro-dropdown">
            <label htmlFor="metro-select">Metro</label>
            <select
              id="metro-select"
              value={selectedMetro}
              onChange={(e) => {
                setSelectedMetro(e.target.value);
                setVisibleHubIds([]);
                setExpandedHubs([]);
              }}
              disabled={isFetchingMetros || !metroTabs.length}
            >
              {!metroTabs.length ? (
                <option value="">
                  {isFetchingMetros ? "Loading metros..." : "No metros available"}
                </option>
              ) : (
                metroTabs.map((metro) => (
                  <option key={metro} value={metro}>
                    {metro}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        <div className="scheduling-assistant-page__header">
          <div className="scheduling-assistant-page__stats">
            <h3>{selectedMetro || "—"}</h3>
            <p>
              Hubs: <strong>{hubList.length}</strong>
            </p>
          </div>
        </div>

        {isReadOnly && (
          <div className="scheduling-assistant-page__readonly-banner">
            🔒 This week is read-only. Navigate to a future week to make edits.
          </div>
        )}

        {isFetchingSchedules && selectedMetroObj && (
          <div className="scheduling-assistant-page__loading">Loading schedules...</div>
        )}

        {!hubList.length && selectedMetro && !isFetchingStores && (
          <div className="scheduling-assistant-page__empty">
            No hubs found for {selectedMetro}. Add hubs in the Better Trucks section.
          </div>
        )}

        {hubList.length > 0 && (
          <div className="scheduling-assistant-page__accordion-wrap">
            <div className="scheduling-assistant-page__accordion-actions">
              <Dropdown
                onSelect={(hubId) => hubId && addHub(hubId)}
                disabled={availableHubsToAdd.length === 0}
              >
                <Dropdown.Toggle variant="outline-primary" size="sm">
                  + Add Hub
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {availableHubsToAdd.map((hub) => (
                    <Dropdown.Item key={hub.id} eventKey={hub.id}>
                      {hub.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="scheduling-assistant-page__accordion">
              <Accordion
                activeKey={expandedHubs}
                onSelect={(keys) => setExpandedHubs(Array.isArray(keys) ? keys : keys ? [keys] : [])}
                alwaysOpen
                flush
              >
                {visibleHubs.map((hub) => {
                  const { schedules: hubSchedules } = schedulesByHub[hub.id] || {
                    hub,
                    schedules: [],
                  };
                  return (
                    <Accordion.Item key={hub.id} eventKey={hub.id}>
                      <Accordion.Header>
                        <span className="scheduling-assistant-page__accordion-title">
                          {hub.name}
                        </span>
                        <span className="scheduling-assistant-page__accordion-badge">
                          {hubSchedules.length} schedule{hubSchedules.length !== 1 ? "s" : ""} this week
                        </span>
                        {visibleHubs.length > 1 && (
                          <button
                            type="button"
                            className="scheduling-assistant-page__accordion-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeHub(hub.id);
                            }}
                            aria-label={`Remove ${hub.name}`}
                          >
                            ×
                          </button>
                        )}
                      </Accordion.Header>
                      <Accordion.Body>
                        <HubScheduleTable
                          hub={hub}
                          hubSchedules={hubSchedules}
                          dayInfo={dayInfo}
                          dayTabs={dayTabs}
                          selectedDay={selectedDay}
                          onDaySelect={setSelectedDay}
                          weekOffset={weekOffset}
                          onWeekChange={(delta) => setWeekOffset((w) => w + delta)}
                          selectedMetro={selectedMetro}
                          selectedMetroObj={selectedMetroObj}
                          onSaveSuccess={refetchSchedules}
                          templateOptions={templateOptions}
                          isReadOnly={isReadOnly}
                          isFetchingTemplates={isFetchingTemplates}
                          upsertSchedule={upsertSchedule}
                          isSaving={isSaving}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </div>
          </div>
        )}
      </div>
    </CardLayout>
  );
};

export default Scheduling;
